import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  useRecoilRefresher_UNSTABLE,
  useRecoilValue,
  useSetRecoilState,
} from 'recoil'
import { MD5 } from 'crypto-js'

import { AwsApi } from 'src/data/aws/AwsService'
import { sideMenuLayoutState } from 'src/data/common/commonAtoms'
import { PersistService } from 'src/data/common/PersistService'
import { authenticatedCustomerState } from 'src/data/customers/CustomersAtoms'
import { CustomerModel } from 'src/data/customers/type'
import { accountPaginatedOrdersSelectorFamily } from 'src/data/orders/OrderAtoms'
import { OrderModel } from 'src/data/orders/type'
import { formatEmail, isProduction, isvalidTimestamp } from 'src/utils/helper'
import { AuthenticationService } from 'src/data/common/AuthenticationService'

function useNoSideMenu(): void {
  const setSidemenuLayout = useSetRecoilState(sideMenuLayoutState)
  useEffect(() => {
    setSidemenuLayout(false)

    return () => {
      setSidemenuLayout(true)
    }
  }, [setSidemenuLayout])
}

function useRefreshOrders(order: OrderModel): void {
  const customer = useRecoilValue(authenticatedCustomerState)
  const refreshOrder = useRecoilRefresher_UNSTABLE(
    accountPaginatedOrdersSelectorFamily({customerId: customer.id, isChild: order.customer.is_child})
  )

  useEffect(() => {
    refreshOrder()
  }, [order, refreshOrder])
}

type ErrorContent = {
  message: string
  status: number
  cloudwatch_link: string
}

function useErrorLogger(error: Error): ErrorContent {
  const [errorLogged, setError] = useState({
    message: '',
    status: 0,
    cloudwatch_link: '',
  })

  useEffect(() => {
    async function getErrorLogged() {
      let errorPayload: ErrorContent = {
        message: '',
        status: 0,
        cloudwatch_link: '',
      }
      try {
        errorPayload = JSON.parse(error.message)
      } catch {
        const customer = PersistService.fetch('authenticated_customer', 'local')
        const cloudWatchLink = await AwsApi.logWebExceptions({
          origin: 'frontend',
          message: error.message,
          status: 400,
          url: window.location.href,
          params: customer
            ? {
                customer_id: (customer as CustomerModel).id,
              }
            : undefined,
          data: error.stack,
        })
        errorPayload = {
          message: `${error.name} - ${error.message}`,
          cloudwatch_link: cloudWatchLink,
          status: 400,
        }
      }
      setError(errorPayload)
    }
    if (!errorLogged.cloudwatch_link) {
      getErrorLogged()
    }
  }, [error, errorLogged.cloudwatch_link])

  return errorLogged
}

function useValidationLoginHashShopify(): () => Promise<boolean> {
  const [search] = useSearchParams()
  const validateLoginHash = async () => {
    const timestamp = search.get('timestamp')
    const email = search.get('customerId')
    const market = isProduction() ? search.get('market') : 'CA'

    if (!isProduction()) {
      return true
    } else if (email && isvalidTimestamp(timestamp) && market) {
      const formattedEmail = formatEmail(email)
      try {
        const secret = await AuthenticationService.getShopifySecret(market)
        const hash = MD5(
          `${formattedEmail}${timestamp}${secret}`
        ).toString()
        //TODO:use hash instead of hash_v2 for the GO LIVE
        const hashedParam = search.get('hash_v2')
        if (hash === hashedParam) {
          return true
        }
      } catch (error) {
        return false
      }
    }
    return false
  }
  return validateLoginHash
}

export {
  useErrorLogger,
  useNoSideMenu,
  useRefreshOrders,
  useValidationLoginHashShopify,
}
